import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './MarketplaceDetailPage.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, ImageModal, TabNavigation, ConfirmationModal, SuccessModal, BackButton, MarketplaceHeartButton, Button } from './shared';
// Issue #101: Removed useGlobalModal - using NavigationContext for create pages
import { useLogout, useUserData } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { formatRelativeTime } from '../utils'; // Issue #108: Shared relative time formatting
import { AdBanner } from './ads';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function MarketplaceDetailPage({ marketplaceObject }) {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();
  // Issue #101: Removed openMarketplaceModal - using NavigationContext for create pages

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [userHasHearted, setUserHasHearted] = useState(false);
  const [heartsCount, setHeartsCount] = useState(0);
  const prevObjectIdRef = useRef();
  const clickTracked = useRef(false);
  // Issue #119: Track images array and index for gallery modal
  const [selectedImageData, setSelectedImageData] = useState(null);
  // Replace native confirm/alert with shared accessible modals
  const [isSoldConfirmOpen, setIsSoldConfirmOpen] = useState(false);
  const [isMarkingSold, setIsMarkingSold] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    // Reset image index when component mounts or marketplace object changes
    setCurrentImageIndex(0);

    // Issue #60: Initialize hearts state from marketplaceObject
    if (marketplaceObject) {
      setUserHasHearted(marketplaceObject.user_has_hearted || false);
      setHeartsCount(marketplaceObject.hearts_count || 0);
      clickTracked.current = false; // Reset click tracking for new object
    }
  }, [marketplaceObject?.id]);

  // Issue #60: Track click when user views detail page
  useEffect(() => {
    const trackClick = async () => {
      if (marketplaceObject && marketplaceObject.id && !clickTracked.current) {
        try {
          await axios.post(`${API_BASE_URL}/api/marketplace/${marketplaceObject.id}/click`);
          clickTracked.current = true; // Mark as tracked
        } catch (err) {
          console.error('Failed to track click:', err);
        }
      }
    };

    trackClick();
  }, [marketplaceObject?.id]);

  // Issue #119: Handle image modal with URL hash for back button support
  useEffect(() => {
    if (selectedImageData) {
      // Modal opened - set URL hash
      window.location.hash = 'image-modal';
    }
  }, [selectedImageData]);

  // Listen for hash changes to close modal when back button is pressed
  useEffect(() => {
    const handleHashChange = () => {
      // If modal is open and hash is no longer #image-modal, close the modal
      if (selectedImageData && window.location.hash !== '#image-modal') {
        setSelectedImageData(null);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [selectedImageData]);

  if (!marketplaceObject) {
    return <div>{t('marketplace.objectNotFound')}</div>;
  }

  // Issue #108: Removed local formatTime - using shared formatRelativeTime from utils

  // Issue #163: Wrap around - go to first image when at last
  const handleNextImage = () => {
    setCurrentImageIndex(prev => {
      const totalImages = marketplaceObject.images.length;
      return (prev + 1) % totalImages;
    });
  };

  // Issue #163: Wrap around - go to last image when at first
  const handlePrevImage = () => {
    setCurrentImageIndex(prev => {
      const totalImages = marketplaceObject.images.length;
      return prev === 0 ? totalImages - 1 : prev - 1;
    });
  };

  const handleSendMessage = () => {
    // Issue #69: Navigate to seller contact page
    navigation.onNavigateToSellerContact?.(marketplaceObject);
  };

  // Issue #60: Toggle heart on marketplace object
  const handleHeartClick = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/marketplace/${marketplaceObject.id}/heart`);
      setUserHasHearted(response.data.user_has_hearted);
      setHeartsCount(response.data.hearts_count);
    } catch (err) {
      console.error('Failed to toggle heart:', err);
    }
  };

  // Issue #64: Mark marketplace object as sold
  // Issue #329: Use correct text based on marketplace_type
  const isSearchType = marketplaceObject.marketplace_type === 'search';

  const handleMarkAsSold = () => {
    setIsSoldConfirmOpen(true);
  };

  const handleCancelMarkSold = () => {
    if (isMarkingSold) return;
    setIsSoldConfirmOpen(false);
  };

  const handleConfirmMarkSold = async () => {
    setIsMarkingSold(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/marketplace/${marketplaceObject.id}/mark-sold`);
      if (response.data.success) {
        setIsSoldConfirmOpen(false);
        setFeedback({
          message: isSearchType ? t('marketplace.markAsFoundSuccess') : t('marketplace.markAsSoldSuccess'),
          variant: 'success',
          navigateAfter: true,
        });
      }
    } catch (err) {
      console.error('Failed to mark as sold:', err);
      setIsSoldConfirmOpen(false);
      setFeedback({
        message: err.response?.data?.message || t('marketplace.markSoldError'),
        variant: 'warning',
        navigateAfter: false,
      });
    } finally {
      setIsMarkingSold(false);
    }
  };

  const handleFeedbackClose = () => {
    const shouldNavigate = feedback?.navigateAfter;
    setFeedback(null);
    if (shouldNavigate) {
      navigation.onNavigateToLandingPage?.();
    }
  };

  return (
    <div className="marketplace-detail-page">
      {/* Header Navigation Bar - Using shared AppHeader component */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <TabNavigation
        activeKey="market"
        tabs={[
          {
            key: 'hot',
            label: t('tabs.hotPosts'),
            onClick: () => {
              localStorage.setItem('landingPageTab', 'hot');
              navigation.onNavigateToLandingPage?.();
            },
          },
          {
            key: 'new',
            label: t('tabs.newPosts'),
            onClick: () => {
              localStorage.setItem('landingPageTab', 'new');
              navigation.onNavigateToLandingPage?.();
            },
          },
          {
            key: 'market',
            label: t('tabs.marketplace'),
            onClick: () => {
              localStorage.setItem('landingPageTab', 'market');
              navigation.onNavigateToLandingPage?.();
            },
          },
        ]}
      />

      {/* Content */}
      <main className="marketplace-detail-content">
        {/* Back Button - Issue #70: Use browser history instead of hardcoded navigation */}
        <BackButton onClick={navigation.onNavigateBack} />

        {/* Author and Time */}
        {/* Issue #220: Show full author name (first and last name) */}
        <div className="object-author-section">
          <span className="author-name">
            {marketplaceObject.author_name}
          </span>
          <span className="created-time">
            {formatRelativeTime(marketplaceObject.created_time, t)}
            {/* Issue #62: Show "edited" mark for edited marketplace objects */}
            {marketplaceObject.is_edited && <span className="edited-mark"> ({t('marketplace.edited')})</span>}
          </span>
        </div>

        {/* Title */}
        <h2 className="object-title">{marketplaceObject.title}</h2>

        {/* Price - Issue #83: Show "Zum Verschenken" for free giveaways */}
        {(marketplaceObject.price || marketplaceObject.is_free_giveaway) && (
          <div className="object-price">
            {marketplaceObject.is_free_giveaway ? t('marketplace.freeGiveaway') : `${marketplaceObject.price} €`}
          </div>
        )}

        {/* Photo Gallery */}
        {marketplaceObject.images && marketplaceObject.images.length > 0 && (
          <div className="photo-gallery">
            <div className="photo-container">
              <img
                src={`${API_BASE_URL}${marketplaceObject.images[currentImageIndex]}`}
                alt={`${marketplaceObject.title} - Foto ${currentImageIndex + 1}`}
                className="gallery-image"
                onClick={() => setSelectedImageData({
                  images: marketplaceObject.images.map(img => `${API_BASE_URL}${img}`),
                  index: currentImageIndex
                })}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              />
            </div>

            {/* Navigation Buttons */}
            {marketplaceObject.images.length > 1 && (
              <>
                <button
                  className="gallery-nav-button gallery-nav-left"
                  onClick={handlePrevImage}
                  aria-label={t('marketplace.previousImage')}
                  style={{
                    position: 'absolute',
                    top: '250px',
                    left: '10px',
                    zIndex: 9999,
                    pointerEvents: 'auto'
                  }}
                >
                  &#8249;
                </button>
                <button
                  className="gallery-nav-button gallery-nav-right"
                  onClick={handleNextImage}
                  aria-label={t('marketplace.nextImage')}
                  style={{
                    position: 'absolute',
                    top: '250px',
                    right: '10px',
                    zIndex: 9999,
                    pointerEvents: 'auto'
                  }}
                >
                  &#8250;
                </button>
              </>
            )}

            {/* Photo Counter */}
            <div className="photo-counter">
              {currentImageIndex + 1} / {marketplaceObject.images.length}
            </div>
          </div>
        )}

        {/* Description */}
        <div className="object-description-section">
          <h3 className="description-title">{t('marketplace.description')}</h3>
          <p className="description-text">{marketplaceObject.post_content}</p>
        </div>

        {/* Issue #60: Action buttons container */}
        <div className="action-buttons-container">
          {/* Issue #174: Hide send message and heart buttons when viewing own object */}
          {userData && marketplaceObject.user_id !== userData.id && (
            <>
              <Button variant="primary" className="send-message-button" onClick={handleSendMessage}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t('marketplace.contactSeller')}</span>
              </Button>

              {/* Issue #60/#385: Shared heart button (disabled for sold objects) */}
              <MarketplaceHeartButton
                variant="large"
                className="heart-button"
                userHasHearted={userHasHearted}
                isSold={!!marketplaceObject.sold_at}
                onToggle={handleHeartClick}
              />
            </>
          )}

          {/* Issue #64: Mark as sold button (only for owner) */}
          {/* Issue #329: Use correct text based on marketplace_type */}
          {userData && marketplaceObject.user_id === userData.id && !marketplaceObject.sold_at && (
            <Button
              variant="secondary"
              fullWidth
              onClick={handleMarkAsSold}
              aria-label={marketplaceObject.marketplace_type === 'search' ? t('marketplace.markAsFound') : t('marketplace.markAsSold')}
            >
              {marketplaceObject.marketplace_type === 'search' ? t('marketplace.markAsFound') : t('marketplace.markAsSold')}
            </Button>
          )}
        </div>
      </main>

      {/* Issue #204: Ad Banner */}
      <AdBanner slot="marketplace-detail-bottom" />

      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="marketplace"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Issue #95: Create Options Menu - Using shared component */}
      {/* Issue #101: Removed onCreatePost and onCreateMarketplace props to use NavigationContext */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />

      {/* Issue #119: Image Modal with horizontal scroll navigation */}
      {selectedImageData && (
        <ImageModal
          images={selectedImageData.images}
          initialIndex={selectedImageData.index}
          onClose={() => {
            setSelectedImageData(null);
            // Remove hash when closing modal without scrolling to top
            if (window.location.hash === '#image-modal') {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          }}
        />
      )}

      {/* Mark as sold/found confirmation - shared accessible modal */}
      <ConfirmationModal
        isOpen={isSoldConfirmOpen}
        onClose={handleCancelMarkSold}
        onConfirm={handleConfirmMarkSold}
        title={isSearchType ? t('marketplace.markAsFound') : t('marketplace.markAsSold')}
        message={isSearchType ? t('marketplace.markAsFoundConfirmShort') : t('marketplace.markAsSoldConfirmShort')}
        confirmText={t('yes')}
        confirmingText={t('marketplace.marking')}
        isConfirming={isMarkingSold}
        variant="default"
      />

      {/* Success / error feedback - shared accessible modal */}
      <SuccessModal
        isOpen={!!feedback}
        onClose={handleFeedbackClose}
        message={feedback?.message || ''}
        variant={feedback?.variant || 'success'}
      />
    </div>
  );
}

export default MarketplaceDetailPage;
